import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/inputs/text-input";
import { RadioGroup } from "@/components/inputs/radio";
import { KycUpgradeError, useUpgradeKYC } from "@/hooks/useUpgradeKYC";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  selectIsKycStandardLevelDialogOpen,
  selectKycNextLevelRequirements,
  selectUserId,
  selectKycLocks,
  closeDialog,
  recordKycAttemptError,
  clearKycAttemptState,
  selectKycErrors,
  clearExpiredLocks,
} from "@/store/profileSlice";
import { GovernmentID } from "@/schemas/kyc";
import Trust from "./trust";
import { ScrollArea } from "@/components/ui/scroll-area";
import { fireCustomMixpanelEvent } from "@/events/fireCustomMixpanelEvent";
import { v4 as uuid } from "uuid";

export const REQUIREMENTS_MAP: Record<GovernmentID, string> = {
  ng_bvn: "BVN",
  ng_vnin: "vNIN",
  ng_nin: "NIN",
  gh_passport: "Passport",
  gh_ssnit: "SSNIT",
  gh_voters_card: "Voters Card",
  gh_drivers_license: "Drivers License",
};

export const getDocumentLabel = (docType: GovernmentID | null) => {
  if (docType) return REQUIREMENTS_MAP[docType];
  return docType;
};

export interface KycFormFields {
  documentVal: string;
  user_id: string;
  tier: string;
  documentType: GovernmentID;
}

const step1Schema = Yup.object().shape({
  documentType: Yup.string().required("Please select a document type"),
});

const step2Schema = Yup.object().shape({
  documentVal: Yup.string()
    .required("Document value is required")
    .trim()
    .test(
      "valid-format",
      "Invalid format for selected document",
      function (value) {
        const { documentType } = this.parent;
        if (!value) return false;

        switch (documentType) {
          case "ng_bvn":
          case "ng_nin":
          case "ng_vnin":
            return /^\d{11}$/.test(value);
          default:
            return value.length > 0;
        }
      },
    ),
});

function ClearErrorOnChange({
  value,
  onChangeDetected,
}: {
  value: string;
  onChangeDetected: () => void;
}) {
  useEffect(() => {
    onChangeDetected();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return null;
}

export function KycStandardLevelDialog() {
  const [step, setStep] = useState<1 | 2>(1);
  const [documentType, setDocumentType] = useState<GovernmentID | null>(null);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const open = useAppSelector(selectIsKycStandardLevelDialogOpen);
  const nextLevelRequirements = useAppSelector(selectKycNextLevelRequirements);
  const userId = useAppSelector(selectUserId);
  const kycLocks = useAppSelector(selectKycLocks);
  const kycErrors = useAppSelector(selectKycErrors);
  const dispatch = useAppDispatch();
  const { mutateAsync } = useUpgradeKYC();

  useEffect(() => {
    if (open) {
      dispatch(clearExpiredLocks());
    }
  }, [open, dispatch]);

  const documentOptions =
    nextLevelRequirements
      ?.filter((docType) => {
        const lock = kycLocks[docType];
        return !lock || Date.now() > lock.lockedUntil;
      })
      ?.map((docType) => ({
        value: docType,
        label: REQUIREMENTS_MAP[docType] || docType,
        id: docType,
      })) || [];

  const activeLock = documentType ? kycLocks[documentType] : null;
  const isOptionCurrentlyLocked = !!(activeLock && Date.now() < activeLock.lockedUntil);

  const handleSubmit = async (
    values: KycFormFields,
    setSubmitting: (isSubmitting: boolean) => void,
  ) => {
    const payload = {
      ...values,
      user_id: userId ?? "",
    };

    try {
      await mutateAsync(payload);

      dispatch(clearKycAttemptState({ documentType: values.documentType }));

      fireCustomMixpanelEvent(
        "CompleteKyc",
        userId,
        uuid(),
        { kyc_level: "standard" },
      ).catch(() => {});

      setSubmissionError(null);
      dispatch(closeDialog());
      setStep(1);
    } catch (error) {
      if (error instanceof KycUpgradeError) {
        const isProc360 = error.source === "Proc360";
        const message = error.validationMessage ?? error.message;

        if (isProc360) {
          const prev = kycErrors[values.documentType];
          const isRepeat = prev?.source === "Proc360" && prev.message === message;

          dispatch(
            recordKycAttemptError({
              documentType: values.documentType,
              source: error.source,
              message,
            }),
          );

          if (isRepeat) {
            // Proc360 twice 
            setSubmissionError(
              "You have been temporarily restricted from verifying after two unsuccessful attempts. Please contact support for help or try again later"
            );
          } else {
            // Proc360 first time
            setSubmissionError(error.message);
          }
        } else {
          // Non-Proc360 (e.g., Kora) -> Immediate lock, show scenario 1 message
          dispatch(
            recordKycAttemptError({
              documentType: values.documentType,
              source: error.source,
              message,
            }),
          );
          setSubmissionError(
            "We were unable to complete your identity verification. Please contact support for help or try again later"
          );
        }
      } else {
        // Network / generic errors
        dispatch(
          recordKycAttemptError({
            documentType: values.documentType,
            source: undefined,
            message: error instanceof Error ? error.message : "unknown_error",
          }),
        );
        setSubmissionError(
          "We were unable to complete your identity verification. Please contact support for help or try again later"
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (value: boolean) => {
    if (!value) {
      dispatch(closeDialog());
      setStep(1);
      setSubmissionError(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="font-outfit">
        <DialogHeader>
          <DialogTitle>
            {step === 1
              ? "Select Document Type"
              : `Verify ${getDocumentLabel(documentType)}`}
          </DialogTitle>
          <DialogDescription>
            {step === 1
              ? "Choose the type of document you want to upload"
              : "Enter your document details"}
          </DialogDescription>
        </DialogHeader>

        <Formik<KycFormFields>
          enableReinitialize
          validationSchema={step === 1 ? step1Schema : step2Schema}
          initialValues={{
            documentVal: "",
            user_id: userId ?? "",
            tier: "standard",
            documentType: documentOptions[0]?.value || ("" as GovernmentID),
          }}
          onSubmit={(values, { setSubmitting }) => {
            if (step === 1) {
              setStep(2);
              setDocumentType(values.documentType);
              setSubmissionError(null);
              setSubmitting(false);
            } else {
              handleSubmit(values, setSubmitting);
            }
          }}
        >
          {({ values, setFieldValue, isSubmitting }) => (
            <Form className="w-full flex flex-col gap-6">
              {step === 1 ? (
                <ScrollArea className="h-[70vh]">
                  <div className="flex flex-col gap-4 mx-2">
                    {documentOptions.length === 0 ? (
                      <div className="p-4 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-xl text-sm text-center">
                        All verification options are temporarily locked due to recent failures. Please contact support or try again later.
                      </div>
                    ) : (
                      <RadioGroup
                        name="documentType"
                        label="Document Type"
                        options={documentOptions}
                        required
                        horizontal={false}
                      />
                    )}
                    <Button
                      type="submit"
                      className="w-full rounded-full"
                      disabled={documentOptions.length === 0}
                    >
                      Next
                    </Button>
                    <Trust />
                  </div>
                </ScrollArea>
              ) : (
                <>
                  <ClearErrorOnChange
                    value={values.documentVal}
                    onChangeDetected={() => setSubmissionError(null)}
                  />

                  {isOptionCurrentlyLocked || submissionError ? (
                    <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">
                     <p className="font-semibold capitalize mb-1">
                        {(isOptionCurrentlyLocked && activeLock?.reason === "proc360_repeat") ||
                        submissionError?.includes("two unsuccessful attempts")
                          ? "verification is temporarily locked"
                          : "verification is temporarily unavailable"}
                     </p>
                      <p>{submissionError}</p>
                    </div>
                  ) : null}

                  <TextInput
                    placeholder={`Enter your ${getDocumentLabel(values.documentType)}`}
                    name="documentVal"
                    label={getDocumentLabel(values.documentType) || ""}
                    autoFocus
                    disabled={isOptionCurrentlyLocked || isSubmitting}
                  />
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full rounded-full"
                      disabled={isSubmitting}
                      onClick={() => {
                        setStep(1);
                        setSubmissionError(null);
                        setFieldValue("documentVal", "");
                      }}
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      className="w-full rounded-full"
                      disabled={isSubmitting || isOptionCurrentlyLocked}
                    >
                      {isSubmitting ? "Verifying..." : `Verify ${getDocumentLabel(values.documentType)}`}
                    </Button>
                  </div>
                </>
              )}
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}